import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext } from 'react';
import { Prompt, useHistory } from 'react-router';
import styled, { css } from 'styled-components';
import Analytics from '../../../../../client/Analytics';
import LINKS from '../../../../../configs/links';
import { cleanseFormErrorObject } from '../../../../../utils/form';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import Container from '../../../../common/base/Container';
import Icon, { IconName } from '../../../../common/base/Icon';
import Text from '../../../../common/base/Text';
import { Div } from '../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { withTeamPermission } from '../../../../contexts/TeamPermissionContext';
import useSearchQuery from '../../../../hooks/useSearchQuery';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavActions, StyledViewNavHeader, ViewBreadcrumbsTitle } from '../../../../layouts/ViewLayout';
import { DashboardContext } from '../../DashboardContext';
import destination_configuration_form_props from '../Forms/destination_configuration';
import resource_details_form_props from '../Forms/resource_details';
import rule_form_props from '../Forms/rules';
import source_configuration_form_props, { SourceConfigurationFormValues } from '../Forms/source_configuration';
import { ResourcesContext } from '../ResourcesContext';
import DestinationSection from './DestinationSection';
import SourceSection from './SourceSection';
const sections = [{
  id: 'source-creation',
  label: 'Define your request source',
  label_types: 'Define your event source',
  description: 'Name and configure the publisher of your HTTP requests, or reuse an existing source. Hookdeck will provide a corresponding endpoint URL.',
  description_types: 'Configure the Hookdeck endpoint representing your HTTP request producer.',
  required: true,
  icon: 'source' as IconName,
  Component: SourceSection
}, {
  id: 'destination-creation',
  label: 'Define your event destination',
  description: 'Name and configure the destination you’d like to route HTTP requests to, or reuse an existing destination.',
  description_types: 'Configure the destination you’d like Hookdeck to route HTTP requests to.',
  required: true,
  icon: 'destinations' as IconName,
  Component: DestinationSection
}, {
  id: 'rules-creation',
  label: 'Define your connection rules',
  description: 'Optionally configure rules that dictate the behavior of events being routed to your destination.',
  description_types: 'Configure rules that dictate the behavior of events routing to your destination.',
  icon: 'rule' as IconName,
  Component: () => <rule_form_props.Fields prefix="rules" display="full" />
}, {
  id: 'connection-creation',
  label: 'Name your connection',
  description: 'Optionally set a name for your connection. Recommended if you have multiple connections with the same source and destination, or if you use the API.',
  description_types: 'Recommended if you have multiple connections with the same source and destination, or if you use the API.',
  icon: 'signature' as IconName,
  Component: () => <resource_details_form_props.Fields prefix="" placeholder="Enter a connection name or leave empty" hide_label name_required={false} />
}, {
  label: 'Create connection',
  label_types: 'Save connection',
  description: 'Create your connection and all associated resources.',
  description_types: 'After you save this connection, we’ll provide the Hookdeck endpoint URL for you to send requests to.',
  icon: 'save' as const,
  icon_types: 'save' as IconName,
  submit: true,
  submit_label: 'Create',
  submit_label_types: 'Save'
}];
export const StyledProgress = styled.div<{
  is_last?: boolean;
  required?: boolean;
  is_last_primary?: boolean;
}>(({
  theme,
  is_last,
  required,
  is_last_primary = false
}) => css`
    height: auto;
    border-left: ${is_last ? 'none' : `1px solid ${theme.colors.surface.base.variant_surface}`};
    width: 1px;
    position: relative;
    left: 12px;
    margin-top: 24px;
    margin-bottom: 0px;
    display: flex;
    justify-content: center;
    span {
      position: absolute;
      top: -21px;
      left: -10px;
      color: ${is_last && !is_last_primary ? theme.colors.on.hue_container.success : required ? theme.colors.on.hue_container.primary : theme.colors.on.neutral.tertiary_neutral};
    }
    &::before {
      content: '';
      position: absolute;
      top: -24px;
      left: -13px;
      display: block;
      border: 1px solid
        ${is_last && !is_last_primary ? theme.colors.outline.success : required ? theme.colors.outline.primary : theme.colors.outline.neutral};
      border-radius: ${theme.radius.xsmall};
      background-color: ${is_last && !is_last_primary ? theme.colors.surface.container.success : required ? theme.colors.surface.container.primary : theme.colors.surface.base.variant_surface_2};
      height: 24px;
      width: 24px;
    }
  `);
const CreateConnectionView = () => {
  const $ = _c(33);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    mutateHasConnection,
    source_types,
    team
  } = useContext(DashboardContext);
  const {
    resource_mutation_methods
  } = useContext(ResourcesContext);
  const history = useHistory();
  const {
    addToast
  } = useToasts();
  const {
    query
  } = useSearchQuery();
  const source_id = query.source_id || null;
  const destination_id = query.destination_id || null;
  const t0 = source_id ? "select" : "new";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ...resource_details_form_props.getInitialValues(),
      ...source_configuration_form_props.getInitialValues()
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = destination_id ? "select" : "new";
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = resource_details_form_props.getInitialValues();
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== team?.max_events_per_second) {
    t4 = destination_configuration_form_props.getInitialValues(undefined, undefined, team?.max_events_per_second);
    $[2] = team?.max_events_per_second;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4) {
    t5 = {
      ...t3,
      ...t4
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== query.transformation_id) {
    t6 = rule_form_props.getInitialValues(undefined, query.transformation_id);
    $[6] = query.transformation_id;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = resource_details_form_props.getInitialValues();
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== destination_id || $[10] !== source_id || $[11] !== t0 || $[12] !== t2 || $[13] !== t5 || $[14] !== t6) {
    t8 = {
      source_id,
      source_use: t0,
      source: t1,
      destination_id,
      destination_use: t2,
      destination: t5,
      rules: t6,
      ...t7
    };
    $[9] = destination_id;
    $[10] = source_id;
    $[11] = t0;
    $[12] = t2;
    $[13] = t5;
    $[14] = t6;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const initial_values = t8;
  let t9;
  if ($[16] !== HookdeckAPI || $[17] !== source_types) {
    t9 = async values => {
      const errors = {
        source_id: values.source_use === "select" && !values.source_id ? "Required" : null,
        source: values.source_use === "new" && values.source ? {
          ...(await resource_details_form_props.validate(values.source, HookdeckAPI.sources.nameIsUsed)),
          ...(await source_configuration_form_props.validate(values.source as SourceConfigurationFormValues, source_types))
        } : null,
        destination_id: values.destination_use === "select" && !values.destination_id ? "Required" : null,
        destination: values.destination_use === "new" && values.destination ? {
          ...(await resource_details_form_props.validate(values.destination, HookdeckAPI.destinations.nameIsUsed)),
          ...(await destination_configuration_form_props.validate(values.destination as any))
        } : null,
        rules: await rule_form_props.validate(values.rules, HookdeckAPI),
        ...(await resource_details_form_props.validate(values, name => values?.source_id && name ? HookdeckAPI.webhooks.nameIsUsed(values.source_id, name) : Promise.resolve(false), false))
      };
      const cleansedErrors = cleanseFormErrorObject(errors);
      return cleansedErrors;
    };
    $[16] = HookdeckAPI;
    $[17] = source_types;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const validate = t9;
  let t10;
  if ($[19] !== HookdeckAPI.webhooks || $[20] !== addToast || $[21] !== history || $[22] !== mutateHasConnection || $[23] !== resource_mutation_methods || $[24] !== source_types) {
    t10 = values_0 => {
      const input = {
        name: values_0.name || null,
        description: values_0.description || null,
        source_id: values_0.source_use === "select" ? values_0.source_id : undefined,
        source: values_0.source_use === "new" ? {
          name: values_0.source.name,
          description: values_0.source.description || null,
          ...source_configuration_form_props.postprocessValues(values_0.source, source_types)
        } : undefined,
        destination_id: values_0.destination_use === "select" ? values_0.destination_id : undefined,
        destination: values_0.destination_use === "new" ? {
          name: values_0.destination.name,
          description: values_0.destination.description || null,
          ...destination_configuration_form_props.postprocessValues(values_0.destination)
        } : undefined,
        rules: rule_form_props.postprocessValues(values_0.rules)
      };
      return HookdeckAPI.webhooks.create(input).then(connection => {
        mutateHasConnection();
        if (resource_mutation_methods) {
          resource_mutation_methods.webhooks.mutate();
          if (!input.destination_id) {
            resource_mutation_methods.destinations.mutate();
          }
          if (!input.source_id) {
            resource_mutation_methods.sources.mutate();
          }
        }
        addToast("success", "Connection created");
        history.push(`/connections?created=${connection.id}`);
      }).catch(e => {
        addToast("error", `An error occurred while saving the connection${e.response?.data[0] ? `: ${e.response.data[0]}` : ""}`);
      });
    };
    $[19] = HookdeckAPI.webhooks;
    $[20] = addToast;
    $[21] = history;
    $[22] = mutateHasConnection;
    $[23] = resource_mutation_methods;
    $[24] = source_types;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const onSubmit = t10;
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ViewBreadcrumbsTitle breadcrumbs={[{
      label: "Create connection",
      highlight: true
    }]} />;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <StyledViewNav><StyledViewNavHeader>{t11}<StyledViewNavActions><Button as="a" outline={true} icon="document" href={LINKS.product_docs.connections} target="_blank">Connections guide</Button><Button outline={true} icon="close" to="/connections" /></StyledViewNavActions></StyledViewNavHeader></StyledViewNav>;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      t: 20
    };
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== initial_values || $[30] !== onSubmit || $[31] !== validate) {
    t14 = <StyledViewLayout><StyledViewContent>{t12}<Container medium={true} m={t13}><Formik initialValues={initial_values} validate={validate} onSubmit={onSubmit}>{_temp2}</Formik></Container></StyledViewContent></StyledViewLayout>;
    $[29] = initial_values;
    $[30] = onSubmit;
    $[31] = validate;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
};
export default withTeamPermission(CreateConnectionView, 'member');
function _temp(location) {
  return location.pathname != "/connections/new" ? "Are you sure you want to quit without saving your work?" : true;
}
function _temp2(t0) {
  const {
    isSubmitting,
    dirty,
    isValid,
    submitCount,
    errors: errors_0,
    handleSubmit
  } = t0;
  return <Form onSubmit={e_0 => {
    if (Object.keys(errors_0).length > 0) {
      Analytics.capture("Create Connection Errors", {
        errors: errors_0
      });
    }
    return handleSubmit(e_0);
  }}><Prompt when={!isSubmitting && dirty} message={_temp} />{sections.map((section, index) => <Div id={section.id} key={index} flex={true} style={{
      position: "relative"
    }}><span id={`section-${section.id}`} style={{
        position: "absolute",
        top: "-100px"
      }} /><StyledProgress is_last={sections.length - 1 === index} is_last_primary={sections.length - 1 === index} required={section.required || section.submit}><Icon p={0.5} icon={section.icon} /></StyledProgress><Div p={{
        l: 11.5,
        b: 20
      }} style={{
        flex: 1,
        minWidth: 0
      }}><Div flex={{
          justify: "space-between",
          align: "center"
        }} w={100}><Div w={section.submit ? 64 : undefined}><Text bold={true} size="l" m={{
              b: 0
            }} flex={{
              align: "center",
              gap: 2
            }}>{section.label_types || section.label}{!section.required && !section.submit && <Badge neutral={true} subtle={true}>Optional</Badge>}</Text><Text as="p" muted={true} m={{
              b: 4
            }}>{section.description_types || section.description}</Text></Div>{section.submit && <Div flex={{
            align: "center",
            gap: 4
          }}>{!isValid && submitCount > 0 && Object.keys(errors_0).length > 0 && <Text danger={true} size="s" flex={{
              align: "center"
            }}><Icon icon="error" left={1} />Fix form errors</Text>}<Button primary={true} icon={isSubmitting ? "loading" : section.icon_types} submit={true} disabled={isSubmitting || submitCount > 0 && !isValid}>{section.submit_label_types || section.submit_label}</Button></Div>}</Div>{section.Component && <section.Component />}</Div></Div>)}</Form>;
}